import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { ghs, num } from "../lib/format";
import { Flame, CheckCircle, TrendDown, TrendUp, ArrowRight } from "@phosphor-icons/react";

export default function AnomalyDigest() {
  const [digest, setDigest] = useState(null);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  useEffect(() => {
    let alive = true;
    api.get("/anomalies/digest").then(({ data }) => alive && setDigest(data)).finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, []);

  if (loading) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-6">
        <div className="brand-progress w-1/3" />
      </div>
    );
  }
  if (!digest) return null;

  const tw = digest.this_week;
  const lw = digest.last_week;
  const delta = tw.count - lw.count;
  const trendUp = delta > 0;

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden card-lift" data-testid="anomaly-digest">
      <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-ps-blue font-semibold">Weekly digest</div>
          <h3 className="font-display font-extrabold text-xl tracking-tight text-ps-deep">Fuel anomaly watch</h3>
        </div>
        <button
          onClick={() => nav("/reports?tab=anomalies")}
          className="text-xs uppercase tracking-wider font-bold text-ps-blue hover:text-ps-deep flex items-center gap-1"
          data-testid="digest-view-all"
        >
          View all <ArrowRight size={14} weight="bold" />
        </button>
      </div>

      {digest.all_clear ? (
        <div className="p-8 text-center" data-testid="digest-all-clear">
          <CheckCircle size={40} className="text-lime-600 mx-auto mb-2" weight="duotone" />
          <div className="font-display font-extrabold text-2xl tracking-tight text-ps-deep">All clear</div>
          <div className="text-sm text-slate-500 mt-1">No pending anomalies this week.</div>
          {lw.count > 0 && (
            <div className="text-xs text-slate-400 mt-3">
              Last week: {lw.count} flagged · {ghs(lw.extra_cost_ghs)}
            </div>
          )}
        </div>
      ) : (
        <div className="p-6 space-y-5">
          <div className="grid grid-cols-3 gap-4">
            <div className="border-r border-slate-200 pr-4">
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1">This week</div>
              <div className="font-display font-extrabold text-3xl tabular text-ps-red flex items-center gap-2">
                {num(tw.count)}
                <Flame size={20} weight="fill" className="text-ps-red" />
              </div>
              <div className="text-[11px] text-slate-500 mt-1">{ghs(tw.extra_cost_ghs)} above baseline</div>
            </div>
            <div className="border-r border-slate-200 pr-4">
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1">Last week</div>
              <div className="font-display font-extrabold text-3xl tabular text-slate-600">{num(lw.count)}</div>
              <div className="text-[11px] text-slate-500 mt-1">{ghs(lw.extra_cost_ghs)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1">Trend</div>
              <div className={`font-display font-extrabold text-3xl tabular flex items-center gap-1 ${trendUp ? "text-ps-red" : "text-lime-700"}`}>
                {trendUp ? <TrendUp size={22} weight="bold" /> : <TrendDown size={22} weight="bold" />}
                {delta > 0 ? "+" : ""}{delta}
              </div>
              <div className="text-[11px] text-slate-500 mt-1">{trendUp ? "Worsening" : delta < 0 ? "Improving" : "Steady"}</div>
            </div>
          </div>

          {/* Severity breakdown */}
          <div className="flex h-2 rounded-full overflow-hidden bg-slate-100" title="Severity mix this week">
            {tw.high > 0 && <div className="bg-ps-red" style={{ flex: tw.high }} title={`${tw.high} high`} />}
            {tw.medium > 0 && <div className="bg-ps-orange" style={{ flex: tw.medium }} title={`${tw.medium} medium`} />}
            {tw.low > 0 && <div className="bg-amber-400" style={{ flex: tw.low }} title={`${tw.low} low`} />}
          </div>
          <div className="flex justify-between text-[10px] uppercase tracking-wider text-slate-500">
            <span>{tw.high} high</span>
            <span>{tw.medium} medium</span>
            <span>{tw.low} low</span>
          </div>

          {/* Top offenders */}
          {digest.top_offenders.length > 0 && (
            <div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-2">Top offenders</div>
              <div className="space-y-1">
                {digest.top_offenders.map((o, i) => (
                  <div key={i} className="flex items-center justify-between text-sm py-1.5 border-b border-slate-100 last:border-0">
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="font-mono-data text-xs text-ps-blue font-semibold">{o.vehicle_number}</span>
                      <span className="text-slate-700 truncate">{o.driver_name}</span>
                      <span className="text-xs text-slate-500 truncate">{o.client}</span>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <span className="font-mono-data tabular text-ps-red font-bold">+{o.variance_pct}%</span>
                      <span className="font-mono-data tabular text-ps-red text-xs">{ghs(o.extra_cost_ghs)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
